import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { authorizedAxios, useAuth } from "@moonscape/shared";

type UseUkSa100Options = {
  taxYear: number;
  enabled?: boolean;
};

export type UkSa100Totals = {
  incomeGBP: number;
  proceedsGBP: number;
  expensesGBP: number;
  incomeEventsCount: number;
};

export type UkSa100Response = {
  taxYear: number;
  taxSetting: any;
  totals: UkSa100Totals;
  incomeEvents: any[];
};

export const useUkSa100 = ({
  taxYear,
  enabled = true,
}: UseUkSa100Options): UseQueryResult<UkSa100Response | null, Error> => {
  const { getToken, user } = useAuth();

  const fetchSa100 = async (): Promise<UkSa100Response | null> => {
    const token = await getToken();
    try {
      const response = await authorizedAxios(token).get(
        "/export/tax-report/uk/sa100",
        {
          params: { year: taxYear },
          validateStatus: (status) =>
            (status >= 200 && status < 300) || status === 204,
        }
      );

      if (response.status === 204) {
        return null;
      }

      return response.data;
    } catch (error: any) {
      const status = error?.response?.status;
      const code = error?.response?.data?.error;
      if (status === 400 && code === "unsupported_jurisdiction") {
        return null;
      }
      throw error;
    }
  };

  return useQuery({
    queryKey: ["uk_sa100", taxYear],
    queryFn: fetchSa100,
    enabled: enabled && !!user?.email && Number.isFinite(taxYear),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });
};
